import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

const MAX_LIST_HEIGHT = 440;

const textOf = (node) =>
  React.Children.toArray(node)
    .map((c) => (typeof c === "string" || typeof c === "number" ? String(c) : c?.props?.children ? textOf(c.props.children) : ""))
    .join("");

// Reads <option> / <optgroup> children into a flat list, so a Select is a drop-in
// for <select>: same children, and onChange gets { target: { value, name } }.
const parse = (children) => {
  const out = [];
  React.Children.toArray(children).forEach((child) => {
    if (!child?.props) return;
    if (child.type === "optgroup") {
      React.Children.toArray(child.props.children).forEach((o, i) => {
        if (o?.props) out.push({ value: String(o.props.value ?? textOf(o.props.children)), label: textOf(o.props.children), disabled: Boolean(o.props.disabled), group: i === 0 ? child.props.label : null, inGroup: true });
      });
    } else if (child.type === "option") {
      out.push({ value: String(child.props.value ?? textOf(child.props.children)), label: textOf(child.props.children), disabled: Boolean(child.props.disabled) });
    }
  });
  return out;
};

// keepOpen: list stays open after a pick (for add-several pickers); closes on outside click / Escape.
const Select = ({ value, onChange, children, className = "", disabled, name, id, style, autoFocus, keepOpen = false, ...rest }) => {
  const options = useMemo(() => parse(children), [children]);
  const current = value === undefined || value === null ? "" : String(value);
  const selected = options.find((o) => o.value === current) || options[0];
  const widest = useMemo(() => options.reduce((a, o) => (o.label.length > a.length ? o.label : a), ""), [options]);

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const typed = useRef({ text: "", at: 0 });

  const close = useCallback(() => setOpen(false), []);

  const choose = (option) => {
    if (!option || option.disabled) return;
    if (!keepOpen) {
      close();
      triggerRef.current?.focus();
    }
    if (option.value !== current) onChange?.({ target: { value: option.value, name } });
  };

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Tall enough for about ten options, within the space on screen.
    const natural = listRef.current?.scrollHeight || 240;
    const below = window.innerHeight - rect.bottom - 18;
    const above = rect.top - 18;
    const openUp = natural > below && above > below;
    const height = Math.min(natural, MAX_LIST_HEIGHT, openUp ? above : below);
    setPos({
      top: openUp ? rect.top - height - 6 : rect.bottom + 6,
      maxHeight: height,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - Math.max(rect.width, 200) - 8)),
      width: Math.max(rect.width, 200),
      origin: openUp ? "bottom left" : "top left",
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setActive(Math.max(0, options.findIndex((o) => o.value === current)));
    const onDown = (e) => {
      if (!listRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) close();
    };
    const onScroll = (e) => !listRef.current?.contains(e.target) && close();
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", onScroll, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && active >= 0) listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const move = (step) => {
    if (!options.length) return;
    let next = active;
    for (let i = 0; i < options.length; i++) {
      next = (next + step + options.length) % options.length;
      if (!options[next].disabled) break;
    }
    setActive(next);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      // Closes the list only, not a dialog it sits in.
      e.nativeEvent.stopPropagation();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(-1);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(options.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      choose(options[active]);
    } else if (e.key === "Tab") {
      close();
    } else if (e.key.length === 1) {
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < 700 ? typed.current.text : "") + e.key.toLowerCase(), at: now };
      const hit = options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(typed.current.text));
      if (hit >= 0) setActive(hit);
    }
  };

  const empty = selected?.value === "";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        autoFocus={autoFocus}
        className={`${className} cc-selectbox${empty ? " is-empty" : ""}`}
        style={style}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        {...rest}
      >
        <span className="cc-selectbox__value">{selected?.label ?? ""}</span>
        <span className="cc-selectbox__sizer" aria-hidden="true">
          {widest}
        </span>
      </button>
      {open &&
        createPortal(
          <ul
            ref={listRef}
            role="listbox"
            className="cc-listbox cc-bb"
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, minWidth: pos?.width, maxHeight: pos?.maxHeight, transformOrigin: pos?.origin }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {options.map((o, i) => (
              <React.Fragment key={`${o.value}-${i}`}>
                {o.group && <li className="cc-listbox__group" role="presentation">{o.group}</li>}
                <li
                  role="option"
                  data-index={i}
                  aria-selected={o.value === current}
                  aria-disabled={o.disabled || undefined}
                  className={`cc-listbox__option${i === active ? " is-active" : ""}${o.value === current ? " is-selected" : ""}${o.inGroup ? " is-grouped" : ""}${o.disabled ? " is-disabled" : ""}${o.value === "" ? " is-empty" : ""}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(o)}
                >
                  <span>{o.label}</span>
                  {o.value === current && <Check size={15} aria-hidden="true" />}
                </li>
              </React.Fragment>
            ))}
          </ul>,
          document.body
        )}
    </>
  );
};

export default Select;
